import { hasStates } from '../library/country-state-picker';
import { Company, CompanyIndustry, CompanySize } from './Company';

export enum ContactType {
  Legal,
  Primary,
  Business,
  Technical,
  Billing,
  CompanyBilling
}
export interface Contact {
  id: string;
  type: ContactType[];
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  userId: string | null; // Is user's contact?
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  industry?: CompanyIndustry;
  size?: CompanySize;
  website?: string;
}
export type ContactFieldName = keyof Contact;

export function isUserContact(contact: Contact): boolean {
  return !!contact.userId;
}

export function isLegalContact(contact: Contact): boolean {
  return contact.type.length === 1 && contact.type[0] === ContactType.Legal;
}

export function isCompanyBillingContact(contact: Contact): boolean {
  return contact.type.length === 1 && contact.type[0] === ContactType.CompanyBilling;
}

export function hasContactType(contact: Contact, contactType: ContactType): boolean {
  return contact.type.includes(contactType);
}

export const EDITABLE_CONTACT_TYPES: ContactType[] = [
  ContactType.Business,
  ContactType.Technical,
  ContactType.Billing
];
export function isContactAdded(contact: Contact): boolean {
  const editableContactTypes: Set<ContactType> = new Set(EDITABLE_CONTACT_TYPES);
  const contactTypes: Set<ContactType> = new Set(contact.type);
  const intersection = new Set(
    [...editableContactTypes].filter((contactType: ContactType): boolean => contactTypes.has(contactType))
  );
  return intersection.size > 0;
}

export function resetContactType(contact: Contact): Contact {
  return {
    ...contact,
    type: contact.type.filter((contactType: ContactType): boolean => !EDITABLE_CONTACT_TYPES.includes(contactType))
  };
}

export type ContactsGroupedByContactTypes = { [contactType in ContactType]: Contact[] };
export function groupContactsByContactTypes(
  contactList: Contact[],
  contactTypes: ContactType[]
): ContactsGroupedByContactTypes {
  const contactListGroupedByContactType: ContactsGroupedByContactTypes = contactTypes
    .reduce((result: { [contactType in ContactType]: Contact[] }, contactType: ContactType) => {
      result[contactType] = [];
      return result;
    }, {} as ContactsGroupedByContactTypes);

  contactList
    .forEach((contact: Contact) => {
      contactTypes.forEach((contactType: ContactType): void => {
        if (contact.type.includes(contactType)) {
          contactListGroupedByContactType[contactType].push(contact);
        }
      });
    });
  return contactListGroupedByContactType;
}

const REQUIRED_FIELD_NAME_FIRST_NAME: ContactFieldName = 'firstName';
const REQUIRED_FIELD_NAME_LAST_NAME: ContactFieldName = 'lastName';
const REQUIRED_FIELD_NAME_EMAIL: ContactFieldName = 'email';
const REQUIRED_FIELD_NAME_CONTACT_TYPE: ContactFieldName = 'type';
const REQUIRED_FIELD_NAME_COUNTRY: ContactFieldName = 'country';
const REQUIRED_FIELD_NAME_STATE: ContactFieldName = 'state';

const REQUIRED_FIELD_NAMES: ContactFieldName[] = [
  REQUIRED_FIELD_NAME_FIRST_NAME,
  REQUIRED_FIELD_NAME_LAST_NAME,
  REQUIRED_FIELD_NAME_EMAIL
];
export function areContactRequiredFieldsFilled(contact: Contact, isExtended?: boolean): boolean {
  const requiredFieldsNames: ContactFieldName[] = [...REQUIRED_FIELD_NAMES];

  if (isExtended) {
    requiredFieldsNames.push(REQUIRED_FIELD_NAME_CONTACT_TYPE);
  }

  if (contact.type.includes(ContactType.Legal) || contact.type.includes(ContactType.CompanyBilling)) {
    requiredFieldsNames.push(REQUIRED_FIELD_NAME_COUNTRY);

    if (contact.country && hasStates(contact.country)) {
      requiredFieldsNames.push(REQUIRED_FIELD_NAME_STATE);
    }
  }

  return requiredFieldsNames.every((fieldName: ContactFieldName): boolean => (
    Array.isArray(contact[fieldName]) ? !!contact[fieldName]?.length : !!contact[fieldName]
  ));
}

export function updateContactFromCompany(contact: Contact, company: Company): Contact {
  return {
    ...contact,
    country: company.country || '',
    state: company.state || '',
    city: company.city || '',
    zipCode: company.zipCode || '',
    address: company.address || '',
    industry: company.industry || undefined,
    size: company.size || undefined,
    website: company.website || ''
  };
}

export function isContactInfoEqual(contact1: Contact, contact2: Contact): boolean {
  return contact1.firstName === contact2.firstName
    && contact1.lastName === contact2.lastName
    && contact1.email === contact2.email
    && contact1.phone === contact2.phone
    && contact1.country === contact2.country
    && contact1.state === contact2.state
    && contact1.city === contact2.city
    && contact1.zipCode === contact2.zipCode
    && contact1.address === contact2.address;
}

export function alignContactInfo(contact1: Contact, contact2: Contact): Contact {
  return {
    ...contact1,
    firstName: contact2.firstName,
    lastName: contact2.lastName,
    email: contact2.email,
    phone: contact2.phone,
    country: contact2.country,
    state: contact2.state,
    city: contact2.city,
    zipCode: contact2.zipCode,
    address: contact2.address
  };
}

export function getDefaultCompanyContactFormValue(): Contact {
  return {
    id: '',
    type: [],
    userId: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: undefined,
    country: undefined,
    state: undefined,
    city: undefined,
    zipCode: undefined,
    address: undefined
  };
}
